import React from "react";
import toast from "react-hot-toast";

import ENDPOINTS from "../../services/Endpoints";
import newInstance from "../../services/newInstance";
import { User } from "../../interfaces/Shared";
import ButtonSpinner from "../../components/Spinner/ButtonSpinner";

interface UserModelProps {
    handleClose: () => void;
    selectedUser?: User;
}

const UserModel:React.FC<UserModelProps> = (props) => {
    const { selectedUser, handleClose } = props;
    const [roles, setRoles] = React.useState<{ id: number, roleTitle: string }[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [user, setUser] = React.useState<User>({
        firstName: selectedUser?.firstName || '',
        lastName: selectedUser?.lastName || '',
        email: selectedUser?.email || '',
        rolePermissionsId: selectedUser?.rolePermissionsId || 3,
        metadata: selectedUser?.metadata || JSON.parse('{}')
    });

    const getUserRoles = async () => {
        try {
            const response = await newInstance.get(ENDPOINTS.getRoles);
            if (response.status === 200) {
                setRoles(response.data);
            }
        } catch (error:any) {
            const errorMessage = error.response?.data?.message || error.message || "An error occurred during fetching";
            toast.error(errorMessage, {
              position: "top-center",
              duration: 3000,
              className: "danger",
            });
        }
    }

    const handleSubmit = (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (selectedUser) {
            updateUser();
        } else {
            createUser();
        }
    }

    const createUser = async () => {
        setLoading(true);
        try {
            const response = await newInstance.post(ENDPOINTS.createUser, user);
            if (response.status === 201) {
                const successMessage = response?.data?.message;
                toast.success(successMessage, {
                  position: "top-center",
                  duration: 3000,
                  className: "success",
                });
                handleClose();
            }
        } catch (error:any) {
            const errorMessage = error.response?.data?.message || error.message || "An error occurred during fetching";
            toast.error(errorMessage, {
              position: "top-center",
              duration: 3000,
              className: "danger",
            });
        } finally {
            setLoading(false);
        }
    }

    const updateUser = async () => {
        setLoading(true);
        try {
            const response = await newInstance.put(`${ENDPOINTS.updateUser}/${selectedUser?.id}`, user);
            if (response.status === 200) {
                const successMessage = response?.data?.message;
                toast.success(successMessage, {
                  position: "top-center",
                  duration: 3000,
                  className: "success",
                });
                handleClose();
            }
        } catch (error:any) {
            const errorMessage = error.response?.data?.message || error.message || "An error occurred during fetching";
            toast.error(errorMessage, {
              position: "top-center",
              duration: 3000,
              className: "danger",
            });
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        getUserRoles();
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl min-h-[50vh] max-h-[95vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r bg-green-800 text-white p-6 flex justify-between items-center">
            <div>
                <h2 className="text-3xl font-bold">{selectedUser ? 'Edit' : 'Create New'} User</h2>
                <p className="text-sm opacity-80">
                    {`Fill in the form below to ${selectedUser ? 'edit a' : 'create a new'} user.`}
                </p>
            </div>
            </div>
            <form className='p-6 flex-1 overflow-y-auto'>
                <div className="w-full flex flex-col md:flex-row items-center gap-4 mb-4">
                    <div className="flex-1">
                        <label className="block text-gray-700 mb-2">First Name</label>
                        <input type="text" name="firstName" value={user?.firstName} className="w-full p-2 border border-gray-300 rounded" placeholder="Enter first name"
                        onChange={(e) => setUser({...user, [e.target.name]:e.target.value})} />
                    </div>

                    <div className="flex-1">
                        <label className="block text-gray-700 mb-2">Last Name</label>
                        <input type="text" name="lastName" value={user?.lastName} className="w-full p-2 border border-gray-300 rounded" placeholder="Enter last name"
                        onChange={(e) => setUser({...user, [e.target.name]:e.target.value})}/>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 mb-2">Email</label>
                    <input type="email" name="email" value={user?.email} className="w-full p-2 border border-gray-300 rounded" placeholder="Enter email"
                    onChange={(e) => setUser({...user, [e.target.name]:e.target.value})} />
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 mb-2">Role</label>
                    <select name="role" defaultValue={String(user.rolePermissionsId)} className="w-full p-2 border border-gray-300 rounded"
                    onChange={(e) => setUser({...user,rolePermissionsId: parseInt(e.target.value, 10)})}>
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                            <option key={role.id} value={String(role.id)}>{role.roleTitle}</option>
                        ))}
                    </select>
                </div>

                <div className="flex justify-end gap-2">
                    <button className="px-4 py-2 text-gray-700 border rounded hover:bg-gray-100 cursor-pointer" onClick={() => handleClose()} >
                    Cancel
                    </button>
                    <button disabled={loading} type="submit" className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800" onClick={handleSubmit}>
                        {loading && <ButtonSpinner />}
                        {selectedUser ? 'Update' : 'Create'} User
                    </button>
                </div>
            </form>

        </div>
    </div>
    );
}

export default UserModel;